import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Moon, Sun } from "lucide-react";
import { toggleTheme, useTheme } from "@/lib/theme";
import { EASE_OUT } from "@/lib/motion";

// testId is a prop because the nav renders one of these in the desktop bar and
// another in the mobile bar, both always in the DOM with one hidden per
// breakpoint. A single hardcoded id matches twice, which is not a cosmetic
// problem: it makes every selector for it ambiguous and forces callers to
// qualify with :visible.
export default function ThemeToggle({ className = "", testId = "theme-toggle" }) {
  const theme = useTheme();
  const reduceMotion = useReducedMotion();
  const isDark = theme === "dark";

  return (
    <button
      type="button"
      onClick={toggleTheme}
      data-testid={testId}
      data-theme={theme}
      // The label says what pressing it does, not what is currently on — a
      // button called "Dark" that turns things light is a small lie.
      aria-label={isDark ? "Switch to light theme" : "Switch to dark theme"}
      title={isDark ? "Switch to light theme" : "Switch to dark theme"}
      className={`relative grid h-9 w-9 shrink-0 place-items-center overflow-hidden rounded-full text-ink shadow-[inset_0_0_0_1.5px_currentColor] transition-[transform,background-color,color] duration-150 ease-out active:scale-[0.94] hover:bg-ink hover:text-paper ${className}`}
    >
      {/* The two icons swap on a short arc — the sun sets, the moon rises.
          Asked for less motion they simply cross-fade: this is decoration on a
          control, and the control has to keep working either way. */}
      <AnimatePresence initial={false} mode="wait">
        <motion.span
          key={theme}
          initial={reduceMotion ? { opacity: 0 } : { y: 14, opacity: 0, rotate: -35 }}
          animate={{ y: 0, opacity: 1, rotate: 0 }}
          exit={reduceMotion ? { opacity: 0 } : { y: -14, opacity: 0, rotate: 35 }}
          transition={{ duration: reduceMotion ? 0.12 : 0.22, ease: EASE_OUT }}
          className="grid place-items-center"
        >
          {isDark ? <Moon size={17} strokeWidth={2.1} /> : <Sun size={17} strokeWidth={2.1} />}
        </motion.span>
      </AnimatePresence>
    </button>
  );
}
